/**
 * Google Analytics Tracking Utilities
 * 
 * Helper functions to track custom events throughout the app.
 * All tracking is privacy-compliant and respects user data.
 */

// Type definitions for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'set',
      targetId: string,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * Track page views manually (for SPA navigation)
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (typeof window.gtag !== 'function') return;
  
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  });
};

/**
 * Track custom events
 * @param eventName - Name of the event (e.g., 'pdf_processed', 'file_uploaded')
 * @param eventParams - Additional parameters for the event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window.gtag !== 'function') return;
  
  window.gtag('event', eventName, eventParams);
};

/**
 * Track PDF processing events
 */
export const trackPDFProcessing = (params: {
  action: 'started' | 'completed' | 'failed';
  filters?: string[];
  pageCount?: number;
  quality?: string;
  layoutRows?: number;
  layoutCols?: number;
  addPageNumbers?: boolean;
  processingTime?: number; // in milliseconds
  errorType?: string;
}) => {
  trackEvent('pdf_processing', {
    event_category: 'PDF Processing',
    event_label: params.action,
    filters: params.filters?.join(','),
    page_count: params.pageCount,
    quality: params.quality,
    layout: params.layoutRows && params.layoutCols ? `${params.layoutRows}x${params.layoutCols}` : undefined,
    add_page_numbers: params.addPageNumbers,
    processing_time_ms: params.processingTime,
    error_type: params.errorType,
  });
};

/**
 * Track PDF merge events
 */
export const trackPDFMerge = (params: {
  action: 'started' | 'completed' | 'failed';
  fileCount?: number;
  totalSize?: number; // in bytes
  mergeTime?: number; // in milliseconds
  errorType?: string;
}) => {
  trackEvent('pdf_merge', {
    event_category: 'PDF Merge',
    event_label: params.action,
    file_count: params.fileCount,
    total_size_mb: params.totalSize ? (params.totalSize / 1024 / 1024).toFixed(2) : undefined,
    merge_time_ms: params.mergeTime,
    error_type: params.errorType,
  });
};

/**
 * Track file uploads
 */
export const trackFileUpload = (params: {
  fileSize: number; // in bytes
  pageCount?: number;
  hasJPEG2000?: boolean;
}) => {
  trackEvent('file_upload', {
    event_category: 'File Upload',
    file_size_mb: (params.fileSize / 1024 / 1024).toFixed(2),
    page_count: params.pageCount,
    has_jpeg2000: params.hasJPEG2000,
  });
};

/**
 * Track downloads
 */
export const trackDownload = (params: {
  fileSize: number; // in bytes
  pageCount?: number;
  processingType?: 'enhance' | 'merge';
}) => {
  trackEvent('file_download', {
    event_category: 'Download',
    file_size_mb: (params.fileSize / 1024 / 1024).toFixed(2),
    page_count: params.pageCount,
    processing_type: params.processingType,
  });
};

/**
 * Track user authentication events
 */
export const trackAuth = (action: 'sign_in' | 'sign_out' | 'sign_up') => {
  trackEvent('user_auth', {
    event_category: 'Authentication',
    event_label: action,
  });
};

/**
 * Track payment events
 */
export const trackPayment = (params: {
  action: 'initiated' | 'completed' | 'failed';
  planName?: string;
  amount?: number;
  currency?: string;
  errorType?: string;
}) => {
  trackEvent('payment', {
    event_category: 'Payment',
    event_label: params.action,
    plan_name: params.planName,
    value: params.amount,
    currency: params.currency || 'USD',
    error_type: params.errorType,
  });
};

/**
 * Track errors
 */
export const trackError = (params: {
  errorType: string;
  errorMessage: string;
  errorLocation?: string;
  fatal?: boolean;
}) => {
  trackEvent('error', {
    event_category: 'Error',
    error_type: params.errorType,
    error_message: params.errorMessage,
    error_location: params.errorLocation,
    fatal: params.fatal || false,
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (featureName: string, action?: string) => {
  trackEvent('feature_usage', {
    event_category: 'Feature',
    feature_name: featureName,
    action: action,
  });
};

/**
 * Track timing/performance metrics
 */
export const trackTiming = (params: {
  category: string;
  variable: string;
  value: number; // in milliseconds
  label?: string;
}) => {
  trackEvent('timing_complete', {
    event_category: params.category,
    name: params.variable,
    value: params.value,
    event_label: params.label,
  });
};
